import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Calendar as CalendarIcon, Clock, ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, CheckCircle2, Loader2, AlertCircle, User, Mail, Phone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const BookingWidget = ({ defaultPlan = '' }) => {
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const [step, setStep] = useState(1); // 1=date, 2=time, 3=details, 4=done
  const [monthCursor, setMonthCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState(null);

  // Build the calendar grid for the month
  const calendarDays = useMemo(() => {
    const first = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1);
    const startWeekday = (first.getDay() + 6) % 7; // Monday-first
    const daysInMonth = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(monthCursor.getFullYear(), monthCursor.getMonth(), d));
    return cells;
  }, [monthCursor]);

  useEffect(() => {
    if (!selectedDate) return;
    setSlotsLoading(true);
    axios.get(`${API}/bookings/slots`, { params: { date: iso(selectedDate) } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [selectedDate]);

  const isPast = (d) => d && d < today;
  const isWeekend = (d) => d && (d.getDay() === 0 || d.getDay() === 6);

  const pickDate = (d) => {
    if (!d || isPast(d) || isWeekend(d)) return;
    setSelectedDate(d);
    setSelectedTime(null);
    setStep(2);
  };

  const pickTime = (t) => {
    setSelectedTime(t);
    setStep(3);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    if (!form.phone.trim()) e.phone = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        date: iso(selectedDate),
        time: selectedTime,
        notes: form.notes,
        plan: defaultPlan,
      };
      const r = await axios.post(`${API}/bookings`, payload);
      setConfirmation(r.data);
      setStep(4);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Booking failed. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Please review your inputs.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedTime(null);
    setForm({ name: '', email: '', phone: '', notes: '' });
    setConfirmation(null);
    setError('');
  };

  return (
    <section id="book" className="py-24 bg-white">
      <div className="max-w-[1080px] mx-auto px-6">
        <div className="text-center mb-12">
          <p className="eyebrow mb-3">FREE CONSULTATION</p>
          <h2 className="section-title text-4xl md:text-5xl">Book Your 30-Minute Session</h2>
          <p className="text-[#556a85] text-lg mt-4 max-w-2xl mx-auto">
            Pick a date and time that works for you. A certified advisor will call at the scheduled slot.
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-3 mb-10 flex-wrap">
          {[
            { n: 1, label: 'Date', icon: CalendarIcon },
            { n: 2, label: 'Time', icon: Clock },
            { n: 3, label: 'Details', icon: User },
            { n: 4, label: 'Confirm', icon: CheckCircle2 },
          ].map((s) => (
            <div key={s.n} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-colors ${
              step >= s.n ? 'bg-[#0a6cb0] text-white border-[#0a6cb0]' : 'bg-white text-[#8ea1b8] border-[#dbe4ee]'
            }`}>
              <s.icon size={14} />
              <span className="font-semibold">{s.n}. {s.label}</span>
            </div>
          ))}
        </div>

        <div className="bg-[#f6f9fc] rounded-2xl border border-[#eef1f5] p-6 md:p-10 shadow-[0_10px_30px_-20px_rgba(10,44,77,0.2)]">
          {step === 1 && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1))}
                  disabled={monthCursor.getFullYear() === today.getFullYear() && monthCursor.getMonth() === today.getMonth()}
                  className="w-10 h-10 rounded-full border border-[#dbe4ee] flex items-center justify-center text-[#0a2c4d] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={18} />
                </button>
                <h3 className="font-serif-display text-2xl font-bold text-[#0a2c4d]">
                  {MONTHS[monthCursor.getMonth()]} {monthCursor.getFullYear()}
                </h3>
                <button
                  onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1))}
                  className="w-10 h-10 rounded-full border border-[#dbe4ee] flex items-center justify-center text-[#0a2c4d] hover:bg-white"
                >
                  <ChevronRight size={18} />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-2 mb-2">
                {WEEKDAYS.map((d) => (
                  <div key={d} className="text-center text-xs uppercase tracking-wider text-[#8ea1b8] font-semibold py-2">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-2">
                {calendarDays.map((d, i) => {
                  if (!d) return <div key={i} />;
                  const past = isPast(d);
                  const weekend = isWeekend(d);
                  const disabled = past || weekend;
                  const isSelected = selectedDate && iso(selectedDate) === iso(d);
                  return (
                    <button
                      key={i}
                      onClick={() => pickDate(d)}
                      disabled={disabled}
                      className={`aspect-square rounded-lg text-sm font-semibold transition-all ${
                        disabled
                          ? 'text-[#c8d1dd] bg-transparent cursor-not-allowed'
                          : isSelected
                          ? 'bg-[#0a6cb0] text-white shadow-md'
                          : 'bg-white text-[#0a2c4d] hover:bg-[#eaf3fb] hover:text-[#0a6cb0] border border-[#eef1f5]'
                      }`}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
              <p className="text-[#8ea1b8] text-xs mt-6 text-center">Weekends unavailable. All times shown in Europe/Rome (CET/CEST).</p>
            </div>
          )}

          {step === 2 && selectedDate && (
            <div>
              <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 text-[#0a6cb0] text-sm font-semibold mb-6 hover:underline">
                <ArrowLeft size={14} /> Back to dates
              </button>
              <h3 className="font-serif-display text-2xl font-bold text-[#0a2c4d] mb-2">
                Pick a time on {selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
              </h3>
              <p className="text-[#556a85] text-sm mb-6">Each slot is 30 minutes. Europe/Rome time.</p>
              {slotsLoading ? (
                <div className="text-center py-10 text-[#8ea1b8]"><Loader2 className="animate-spin inline mr-2" size={18} /> Loading slots...</div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                  {slots.map((s) => (
                    <button
                      key={s.time}
                      onClick={() => s.available && pickTime(s.time)}
                      disabled={!s.available}
                      className={`py-3 rounded-lg text-sm font-semibold border transition-all ${
                        !s.available
                          ? 'bg-[#f0f3f8] text-[#c8d1dd] border-[#eef1f5] line-through cursor-not-allowed'
                          : selectedTime === s.time
                          ? 'bg-[#0a6cb0] text-white border-[#0a6cb0]'
                          : 'bg-white text-[#0a2c4d] border-[#eef1f5] hover:border-[#0a6cb0] hover:text-[#0a6cb0]'
                      }`}
                    >
                      {s.time}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && selectedDate && selectedTime && (
            <form onSubmit={submit}>
              <button type="button" onClick={() => setStep(2)} className="inline-flex items-center gap-2 text-[#0a6cb0] text-sm font-semibold mb-6 hover:underline">
                <ArrowLeft size={14} /> Change time
              </button>

              <div className="bg-[#0a2c4d] text-white rounded-xl p-5 mb-8 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/60 mb-1">Selected slot</p>
                  <p className="font-serif-display text-xl font-bold">
                    {selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} &middot; {selectedTime}
                  </p>
                </div>
                <span className="text-xs bg-white/15 px-3 py-1 rounded-full">Europe/Rome</span>
              </div>

              <h3 className="font-serif-display text-2xl font-bold text-[#0a2c4d] mb-1">Your details</h3>
              <p className="text-[#556a85] text-sm mb-6">We&apos;ll send a confirmation email once you book.</p>

              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <Field label="Full name" icon={User} error={errors.name}>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" className={inputCls(errors.name)} />
                </Field>
                <Field label="Email" icon={Mail} error={errors.email}>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" className={inputCls(errors.email)} />
                </Field>
              </div>
              <div className="mb-5">
                <Field label="Phone" icon={Phone} error={errors.phone}>
                  <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39 380 645 7462" className={inputCls(errors.phone)} />
                </Field>
              </div>
              <div className="mb-6">
                <Field label="Notes (optional)">
                  <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything you'd like the advisor to know?" className={inputCls(false)} />
                </Field>
              </div>

              {error && (
                <div className="mb-5 flex items-center gap-2 text-[#a83232] bg-[#fdecec] px-4 py-3 rounded-lg text-sm">
                  <AlertCircle size={18} /> <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#0a6cb0] text-white font-semibold uppercase tracking-wider text-sm hover:bg-[#0a86d4] transition-colors disabled:opacity-70"
              >
                {submitting ? (<><Loader2 size={16} className="animate-spin" /> Booking...</>) : (<>Confirm Booking <ArrowRight size={16} /></>)}
              </button>
            </form>
          )}

          {step === 4 && confirmation && (
            <div className="text-center py-8">
              <div className="w-20 h-20 rounded-full bg-[#e6f5ed] text-[#0a7a4d] mx-auto flex items-center justify-center mb-6">
                <CheckCircle2 size={40} />
              </div>
              <h3 className="font-serif-display text-3xl font-bold text-[#0a2c4d] mb-3">You&apos;re all set!</h3>
              <p className="text-[#556a85] text-base max-w-xl mx-auto mb-6">
                Your consultation is booked for <strong className="text-[#0a2c4d]">{confirmation.date}</strong> at <strong className="text-[#0a2c4d]">{confirmation.time}</strong> (Europe/Rome).
                A confirmation email has been sent to <strong className="text-[#0a2c4d]">{confirmation.email}</strong>.
              </p>
              <button onClick={resetAll} className="pill-btn pill-btn-outline">Book another slot</button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, icon: Icon, error, children }) => (
  <label className="block">
    <span className="flex items-center gap-2 text-[#0a2c4d] text-sm font-semibold mb-2">
      {Icon && <Icon size={14} className="text-[#0a6cb0]" />} {label}
    </span>
    {children}
    {error && <span className="block text-red-500 text-xs mt-1">{error}</span>}
  </label>
);

const inputCls = (err) =>
  `w-full px-4 py-3 rounded-lg border bg-white text-[#0a2c4d] placeholder-[#a2b3c8] text-sm focus:outline-none focus:ring-2 focus:ring-[#0a6cb0]/30 transition-colors ${
    err ? 'border-red-300' : 'border-[#dbe4ee] focus:border-[#0a6cb0]'
  }`;

export default BookingWidget;
